<template>
  <div>
    <es-loader
      v-if="loading"
      style="margin: 6.5rem"
    />
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <template v-else>
      <es-filter :defaultExpanded="filterExpanded">
        <template #filter-options>
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-2">
              <div class="field">
                <label class="label has-text-weight-bold">
                  Batch ID :
                </label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="batchIdInput"
                  >
                </div>
              </div>
            </div>
            <div class="column is-2">
              <es-select
                keyValue="id"
                label="Submission Type : "
                ref="selSubmissionType"
                value-tpl="${description}"
                :multi="true"
                :preSelected="matchTypeIdsSelected"
                :setValues="MATCHTYPES"
                :clearOut="true"
                @update:values="matchTypeIds = $event"
              />
            </div>
            <div class="column is-2">
              <es-select
                keyValue="code"
                label="Match Type :"
                ref="selPrograms"
                valueTpl="${description}"
                :clearOut="true"
                :multi="true"
                :preSelected="programs"
                :setValues="indexProgramsNonDCX"
                @update:values="programs = $event"
              />
            </div>
            <div class="column is-2">
              <es-select
                keyValue="id"
                label="Status :"
                ref="selStatuses"
                valueTpl="${description}"
                :clearOut="true"
                :multi="true"
                :preSelected="statusInput"
                :setValues="statuses"
                @update:values="statusInput = $event"
              />
            </div>
          </div>
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-2">
              <es-select
                api="districts"
                keyValue="code"
                ref="selDistricts"
                searchMinLength="2"
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:DISTRICT_CODE`)} :`"
                :multi="true"
                :search="true"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                :preSelected="districtCodes"
                @update:values="districts = $event"
              />
            </div>
            <div class="column is-2">
              <es-select
                api="schools"
                :key-value="['districtCode', 'code']"
                ref="selSchools"
                searchMinLength="2"
                :apiQueryParams="getDistrictParams"
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_CODE`)} :`"
                :multi="true"
                :preSelected="schoolCodesPromises"
                :search="true"
                :subValueTplFn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                @update:values="schools = $event"
              />
            </div>
            <div class="column is-2">
              <es-select
                keyValue="id"
                ref="selYears"
                valueTpl="${year}"
                :clearOut="true"
                :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_YEAR`)} :`"
                :multi="true"
                :preSelected="schoolYearInput"
                :setValues="years"
                @update:values="schoolYearInput = $event"
              />
            </div>
            <div class="column is-4">
              <div class="field no-border">
                <label class="label has-text-weight-bold">
                  Process Date Range :
                </label>
                <div class="control is-flex">
                  <input
                    class="input"
                    type="date"
                    v-model="startDateInput"
                  >
                  <span
                    class="is-flex flex-align--center"
                    style="padding: 0 0.5rem; font-size: 12px"
                  >
                    to
                  </span>
                  <input
                    class="input"
                    type="date"
                    v-model="endDateInput"
                  >
                </div>
              </div>
            </div>
            <div
              class="column"
              style="margin-top: auto; margin-left: auto"
            >
              <div class="field is-grouped flex-justify--end">
                <div class="control is-flex flex-align--center">
                  <a @click="resetFilter">
                    Reset
                  </a>
                </div>
                <div class="control">
                  <button
                    class="button is-primary"
                    :disabled="isStateUser && !schoolYearInput.length"
                    :title="isStateUser && !schoolYearInput.length
                      ? 'School year is required.' : null"
                    @click="filterData"
                  >
                    Filter Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-filter>
      <es-exception
        v-if="isStateUser && !schoolYear.length"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="School Year must be entered to view results."
      />
      <es-loader
        v-else-if="loadingBatches"
        style="margin: 6.5rem"
      />
      <es-exception
        v-else-if="!loadingBatches && loadingBatchError"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-exception
        v-else-if="!loadingBatches && !loadingBatchError && !batches.length"
        style="padding: 6.5rem" margin="0rem"
        exceptionMessage="No results found."
      />
      <div
        v-else-if="!loadingBatches && !loadingBatchError && batches.length"
        class="content"
      >
        <dm-batches
          :batchList="batches"
          :totalCount="batchesTotalCount"
          :columnHeaders="columnHeaders"
          :apiSorter="apiSorter"
          @updateBatchColumnHeaders="updateSorter"
          />
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { mapActions, mapGetters } from 'vuex';

import DmBatches from '@/components/DmBatches.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsException from '@/components/EsException.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsSelect from '@/components/EsSelect.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    DateUtils,
    FieldLabels,
  ],
  components: {
    DmBatches,
    EsLoader,
    EsException,
    EsFilter,
    EsSelect,
  },
  props: {
    batchId: {
      type: String,
    },
    districtCodes: {
      type: Array,
    },
    endDate: {
      type: String,
    },
    page: {
      type: String,
    },
    programCodes: {
      type: Array,
    },
    matchTypeId: {
      type: Array,
    },
    schoolCodes: {
      type: Array,
    },
    schoolYear: {
      type: Array,
    },
    status: {
      type: Array,
    },
    startDate: {
      type: String,
    },
    sortField: {
      type: String,
      default: 'id',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
  },
  data() {
    const STATUSES = [
      { status: ['COMPLETED'], description: 'Completed', id: 1 },
      { status: ['ERRORED', 'ERRORED_VALIDATION'], description: 'Errors', id: 2 },
      { status: ['RUNNING'], description: 'In Progress', id: 3 },
      { status: ['COMPLETED_WITH_PENDING'], description: 'Near Matches', id: 5 },
      // TODO: Add Near Match System Label?
      { status: ['QUEUED'], description: 'Request Queued', id: 6 },
      { status: ['LOADED'], description: 'Request Received', id: 7 },
    ];
    return {
      // props and filter
      batchIdInput: this.batchId,
      districts: this.districtCodes.map(d => ({ code: d })),
      endDateInput: this.endDate,
      initialDate: null,
      matchTypeIds: [],
      MATCHTYPES: null,
      programs: [],
      schools: this.schoolCodes.map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),
      schoolYearInput: null,
      startDateInput: this.startDate,
      statusInput: STATUSES.filter(s => this.status.some(ai => s.status.includes(ai))),
      statuses: STATUSES,
      years: [],

      // data for batch view
      batches: [],
      batchesTotalCount: null,
      filterExpanded: true,
      loading: true,
      loadingBatches: false,
      loadingError: null,
      loadingBatchError: null,
      userPersonTypesCodes: null,

      apiSorter: {},
      columnHeaders: [
        {
          header: null,
          field: null,
          style: {
            width: '55px',
          },
        },
        {
          header: 'Batch Info',
          title: 'Batch Info',
          field: 'id',
          order: 'DESC',
          sortable: true,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Match Type',
          title: 'Match Type',
          field: null,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Location Info',
          title: 'Location Info',
          field: null,
          style: {
            width: '20%',
          },
        },
        {
          header: 'Process Info',
          title: 'Process Info',
          field: 'lastUpdateDate',
          order: null,
          sortable: true,
          style: {
            idth: '10%',
          },
        },
        {
          header: 'Record Info',
          title: 'Record Info',
          field: null,
          style: {
            width: '10%',
          },
        },
        {
          header: 'Status Info',
          title: 'Status Info',
          field: 'status',
          order: null,
          sortable: true,
        },
        {
          header: 'Actions',
          title: 'Actions',
          field: null,
          style: {
            width: '1%',
          },
        },
      ],
    };
  },
  async created() {
    try {
      await Promise.all([
        this.loadPrograms(),
        this.loadDmSchoolYears(),
        this.loadMatchTypes('batch'),
        this.loadPersonTypes(),
      ]);
      this.programs = this.indexProgramsNonDCX
        .filter(({ code }) => this.programCodes.includes(code));
      this.years = this.dmSchoolYearsDesc;
      this.schoolYearInput = this.years.filter(y => this.schoolYear.find(sy => sy === y.year));
      this.matchTypeIds = this.matchTypeIdsSelected;
      if (this.schoolYear) {
        this.loadBatches();
      }
    } catch (e) {
      this.loadingError = true;
    } finally {
      this.loading = false;
    }
    this.initialDate = this.$route.query.startDate ? new Date(this.$route.query.startDate) : null;
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
  },
  mounted() {
    this.$root.$on('refreshMatchHistory', this.loadBatches);
  },
  destroyed() {
    this.$root.$off('refreshMatchHistory');
  },
  watch: {
    $route() {
      if (!this.isStateUser || this.schoolYear.length) {
        this.loadBatches();
      }
    },
  },
  asyncComputed: {
    async schoolCodesPromises() {
      const promises = [];
      this.schoolCodes.forEach(code => promises.push(axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`)));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters({
      isStateUser: 'isStateUser',
    }),
    ...mapGetters('lookups', [
      'indexProgramsNonDCX',
      'dmSchoolYearsDesc',
      'defaultPersonType',
    ]),
    dateDifference() {
      if (!this.initialDate) {
        return 29;
      }
      // Get today's date
      const today = new Date();
      // Calculate the time difference in milliseconds
      const timeDifference = today - this.initialDate;
      // Convert time difference from milliseconds to days
      const dayDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      // Return the difference in days
      return dayDifference;
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);
      return params;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    hasInputParams() {
      return this.batchIdInput || this.programs.length || this.districts.length || this.endDateInput
        || this.page || this.schools.length || this.startDateInput || this.schoolYearInput.length
        || this.statusInput.length || this.matchTypes.length;
    },
    hasPropIds() {
      return this.batchId;
    },
    matchTypeIdsSelected() {
      return this.MATCHTYPES.filter(m => this.matchTypeId.includes(m.id));
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
    searchParams() {
      const params = {};
      if (this.batchIdInput) {
        params.batchId = this.batchIdInput.trim();
      }
      if (this.matchTypeIds.length) {
        params.matchTypeIds = this.matchTypeIds.map(m => m.id);
      }
      if (this.programs.length) {
        params.programCode = this.programs.map(p => p.code);
      }
      if (this.statusInput.length) {
        const arr = this.statusInput.map(s => s.status);
        params.status = arr.flat();
      }
      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }
      if (this.schools.length) {
        params.schoolCode = this.schools.map(s => `${s.code}:${s.districtCode}`);
      }
      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput.map(y => y.year);
      }
      if (this.startDateInput) {
        params.startDate = this.startDateInput;
      }
      if (this.endDateInput) {
        params.endDate = this.endDateInput;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};
      params.page = this.page ? this.page : undefined;
      params.ids = this.batchId ? this.batchId : undefined;
      params.programCodes = this.programCodes.length ? this.programCodes : undefined;
      params.districtCodes = this.districtCodes.length ? this.districtCodes : undefined;
      params.schoolCodes = this.schoolCodes.length ? this.getSchoolCodes() : undefined;
      params.schoolYear = this.schoolYear.length ? this.schoolYear : undefined;
      params.statuses = this.status.length ? this.status.flat() : undefined;
      params.startDate = this.startDate ? this.startDate : undefined;
      params.matchTypeIds = this.matchTypeIds.length
        ? this.matchTypeIds.map(c => c.id) : undefined;
      params.endDate = this.endDate ? this.endDate : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPrograms',
      'loadDmSchoolYears',
      'loadPersonTypes',
    ]),
    async loadMatchTypes(type) {
      const filter = type ? `?filter=${type}` : '';
      const res = await axios.get(`matchtypes${filter}`);
      this.MATCHTYPES = res.data;
    },
    getSchoolCodes() {
      return this.schoolCodes.map(code => code.split(/[:]+/)[0]);
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
    filterData() {
      if (!this.isStateUser || this.hasInputParams) {
        this.$router.replace({ query: this.searchParams });
      } else if (!this.hasInputParams && this.searchParams) {
        this.resetFilter();
      }
    },
    getBatches() {
      return axios.get('directmatch/batches',
        {
          params: this.urlParams,
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
    },
    resetFilter() {
      // TODO: searchParams() returns all keys (even if the value is undefined)
      if (Object.keys(this.urlParams).length) {
        this.$router.replace({ query: {} });
      }

      this.$refs.selDistricts.clear();
      this.$refs.selSchools.clear();
      this.$refs.selSubmissionType.clear();
      this.$refs.selPrograms.clear();
      this.$refs.selStatuses.clear();
      this.$refs.selYears.clear();

      this.batchIdInput = '';
      this.startDateInput = '';
      this.endDateInput = '';

      this.batches = [];
      this.batchesTotalCount = null;
      this.$emit('isPageLoaded', false);

      this.$refs.selYears.pushValues(this.years.filter(y => this.$store.state.propertiesModule.properties['RULE-CURRENT_SCHOOL_YEAR'] === y.year));
      this.startDateInput = this.buildDateString(this.dateDifference);
      this.filterData();
    },
    async loadBatches() {
      try {
        this.$emit('isPageLoaded', false);
        this.loadingBatches = true;
        this.loadingBatchError = false;
        this.batchesTotalCount = null;

        const response = await this.getBatches();
        this.batches = response.data.data;
        this.batchesTotalCount = response.data.totalCount;
        [this.apiSorter] = response.data.sorters;
        this.$emit('isPageLoaded', true);
      } catch (e) {
        this.loadingBatchError = true;
      } finally {
        this.loadingBatches = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
